import { useState } from "react";
import { Loader2, Send, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  askJurisIA,
  getApiErrorDetail,
  type RAGSource,
} from "@/lib/api";
import { classifyApiError } from "@/hooks/useApiErrorState";

interface AssistantIAPanelProps {
  /** Si fourni, transmis dans le payload pour audit (ne filtre pas le RAG cote core). */
  caseFileId?: number;
  /** Avertissement custom (delibere, instruction, etc). Defaut : etancheite generique. */
  scopeHint?: string;
}

// Panneau Assistant IA "etanche" -- consomme par l'ArbiterShell en
// rightPanel. Pose explicitement le contrat "secret du delibere" en
// haut : aucune notification vers l'exterieur, l'IA n'envoie rien aux
// parties. Branche sur POST /api/v1/recherche/ask (gate Art. 52 cote
// backend -- erreur classifiee remontee inline).
export function AssistantIAPanel({
  caseFileId,
  scopeHint,
}: AssistantIAPanelProps) {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [sources, setSources] = useState<RAGSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isArt52, setIsArt52] = useState(false);

  const onSubmit = async () => {
    const q = question.trim();
    if (q.length < 3 || loading) return;
    setLoading(true);
    setError(null);
    setIsArt52(false);
    try {
      const res = await askJurisIA(q, caseFileId);
      setAnswer(res.answer);
      setSources(res.sources_used);
    } catch (err) {
      const c = classifyApiError(err);
      if (c.kind === "art52") {
        setIsArt52(true);
        setError(c.detail);
      } else if (c.kind !== "none") {
        setError(c.detail);
      } else {
        setError(getApiErrorDetail(err) ?? "Service IA indisponible.");
      }
      setAnswer(null);
      setSources([]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      void onSubmit();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles size={14} style={{ color: "var(--ai-violet)" }} />
          <h3 className="text-h3-arbitre">Assistant IA</h3>
        </div>
        <p
          className="text-xs text-muted-foreground mt-1"
          style={{ lineHeight: 1.55 }}
        >
          <ShieldCheck
            size={11}
            className="inline mr-1"
            style={{ color: "var(--ai-violet)" }}
          />
          {scopeHint ??
            "Espace étanche — secret du délibéré. Aucune sortie vers les parties."}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {answer && (
          <div
            className="text-xs p-3 rounded-md"
            style={{
              background: "var(--graphite-700)",
              border: "1px solid var(--graphite-600)",
              lineHeight: 1.55,
            }}
          >
            <div
              className="text-caption-arbitre mb-2"
              style={{ color: "var(--ai-violet)" }}
            >
              Réponse
            </div>
            <p className="whitespace-pre-wrap text-foreground">{answer}</p>
            {sources.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <div
                  className="text-caption-arbitre mb-2"
                  style={{ color: "var(--proof-gold)" }}
                >
                  Sources ({sources.length})
                </div>
                <ul className="space-y-2">
                  {sources.map((s) => (
                    <li
                      key={s.id}
                      className="text-[11px]"
                      style={{ color: "var(--arbitre-slate-300)", lineHeight: 1.5 }}
                    >
                      <code
                        className="gsf-mono-proof block mb-1 text-[10px]"
                        style={{ color: "var(--proof-gold)" }}
                      >
                        {s.source}
                      </code>
                      {s.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {error && (
          <div
            className="text-xs p-3 rounded-md"
            style={{
              background: isArt52
                ? "color-mix(in oklch, var(--alert-orange) 12%, transparent)"
                : "color-mix(in oklch, var(--block-red) 15%, transparent)",
              color: isArt52 ? "var(--alert-orange)" : "rgb(254 202 202)",
              border: isArt52
                ? "1px solid color-mix(in oklch, var(--alert-orange) 35%, transparent)"
                : "1px solid color-mix(in oklch, var(--block-red) 35%, transparent)",
              lineHeight: 1.5,
            }}
          >
            {isArt52 && (
              <strong className="block mb-1">
                Art. 52 AI Act — profil non vérifié
              </strong>
            )}
            {error}
          </div>
        )}

        {!answer && !error && !loading && (
          <p
            className="text-xs italic"
            style={{ color: "var(--arbitre-slate-500)", lineHeight: 1.6 }}
          >
            Posez une question sur le dossier ou le corpus OHADA. L'IA cite
            ses sources. Conformité AI Act Art. 13 : chaque échange est
            journalisé dans votre registre IA personnel (purgeable depuis
            le profil).
          </p>
        )}
      </div>

      <div className="border-t border-border p-3 space-y-2">
        <Textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          rows={3}
          placeholder="Ex : conditions d'application de l'art. 13 AUDA ?"
          disabled={loading}
          className="resize-none text-sm"
        />
        <div className="flex items-center justify-between gap-2">
          <span
            className="text-[10px]"
            style={{ color: "var(--arbitre-slate-500)" }}
          >
            ⌘ + Enter pour envoyer
          </span>
          <Button
            onClick={() => void onSubmit()}
            disabled={loading || question.trim().length < 3}
            size="sm"
            style={{
              background: "var(--ai-violet)",
              color: "var(--arbitre-slate-100)",
            }}
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin mr-2" size={14} />
                Génération…
              </>
            ) : (
              <>
                <Send size={14} className="mr-2" />
                Soumettre
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
